import { z } from "zod";

/**
 * Manual dispatch/override — Flows §5: hub manager or admin assigns the
 * next FIFO rider to a booking. The rider is chosen server-side
 * (assign_rider); the client only names the booking + hub.
 */
export const assignBookingSchema = z.object({
  bookingId: z.string().uuid(),
  hubId: z.string().uuid(),
});

export type AssignBookingInput = z.infer<typeof assignBookingSchema>;

/**
 * Override on top of the FIFO above — hub manager/admin puts a SPECIFIC
 * rider on a specific booking ("move this rider onto this client's job"),
 * bypassing assign_rider's queue order. Only reachable pre-pickup
 * (booking status awaiting_rider/dispatched); admin_assign_rider (0053)
 * enforces the rest server-side — hub match, rider availability, and
 * refusing to bump a rider who already has a live trip.
 */
export const assignSpecificRiderSchema = z.object({
  bookingId: z.string().uuid(),
  riderId: z.string().uuid(),
});

export type AssignSpecificRiderInput = z.infer<typeof assignSpecificRiderSchema>;

/**
 * Cancel a rider's current dispatch without picking a replacement — either
 * a genuine "wrong rider, undo it" or the recovery path for a rider wedged
 * at dispatch_queue.status='assigned' with no live booking behind it (the
 * "can't clock out" bug 0052 stops at the source; this is the manual fix for
 * rows already stuck before that fix, or any other cause). Pre-pickup only —
 * admin_release_rider_dispatch (0053) refuses once a live trip exists.
 */
export const releaseRiderDispatchSchema = z.object({
  reason: z.string().min(3).max(500),
});

export type ReleaseRiderDispatchInput = z.infer<typeof releaseRiderDispatchSchema>;
